import React, { useEffect, useState, useMemo } from "react";
import Dropdown from "../reusable/Dropdown";
import Pagination from "../reusable/Pagination";
import BlueDot from "../../assets/svg/BlueDot.svg";
import GrayDot from "../../assets/svg/GrayDot.svg";
import OrangeDot from "../../assets/svg/OrangeDot.svg";
import Loader from "../../components/Live/Loader";
import { useNavigate } from "react-router-dom";
import { MdDeleteOutline } from "react-icons/md";
import { FiEdit, FiRefreshCw } from "react-icons/fi";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import ActionsDropdown from "../reusable/ActionsDropdown";
import { useSelector, useDispatch } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { hasPermission, hasRole, showToast } from "../../utils/utils";
import { deleteCourse, selectCourseDeleteStatus, selectCourseDeleteError } from "../../features/courses/courseSlice";
import { API } from "../../shared/api/Api";
import { saveAs } from 'file-saver';
import axios from "axios";
import { ReactComponent as ExcelIcon } from '../../assets/svg/Excel.svg';

/**
 * CourseTable Component
 * Refactored to use navigation instead of modals
 */
const CourseTable = ({
  activeTab,
  setActiveTab,
  courses,
  categories,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  isStudent,
  setSelectedItem,
  loader,
  setReFetchData,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const deleteStatus = useSelector(selectCourseDeleteStatus);
  const deleteError = useSelector(selectCourseDeleteError);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Close other dropdowns when one is opened
  const handleDropdownToggle = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  // Determine table tabs based on user role
  const determinedTableTabs = useMemo(() => {
    let tabs = [];
    const isUserSuperAdmin = user.roles && hasRole(user.roles, "super-admin");

    if (isUserSuperAdmin) {
      tabs = ["All Courses"];
    } else if (user.isStudent) {
      tabs = ["My Courses", "All Courses"];
    } else if (user.isInstructor) {
      tabs = ["My Courses"];
    } else {
      tabs = ["All Courses"];
    }
    return tabs;
  }, [user]);

  // Effect to sync activeTab with available tabs
  useEffect(() => {
    if (
      determinedTableTabs.length > 0 &&
      !determinedTableTabs.includes(activeTab)
    ) {
      setActiveTab(determinedTableTabs[0]);
    }
  }, [activeTab, setActiveTab, determinedTableTabs]);

  // Handle delete with confirmation
  const handleDelete = async (courseId) => {
    if (window.confirm("Are you sure you want to delete this course? When you delete the course, The system will delete everything related to it.")) {
      const result = await dispatch(deleteCourse({ token: user.token, courseId }));
      
      if (deleteCourse.fulfilled.match(result)) {
        showToast('Success', 'Course deleted successfully', 'success');
        if (setReFetchData) {
          setReFetchData(true);
        }
      } else if (deleteCourse.rejected.match(result)) {
        showToast('Error', result.payload || 'Failed to delete course', 'error');
      }
    }
  };

  // Handle delete errors
  useEffect(() => {
    if (deleteStatus === 'failed' && deleteError) {
      showToast('Error', deleteError, 'error');
    }
  }, [deleteStatus, deleteError]);


  // Excel export handler
  const exportToExcel = async () => {
    try {
      const response = await axios.get(API.Courses.app_courses_export_to_excel, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      let filename;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        filename = disposition.split('filename=')[1].trim().replace(/['"]/g, '');
      } else {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, '0');
        const formatted = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
        filename = `courses_${formatted}.xlsx`;
      }
      saveAs(response.data, filename);
      showToast('Success', 'Courses exported successfully', 'success');
    } catch (error) {
      showToast(
        'Export Failed',
        error?.response?.data?.message || 'Could not export courses. Please try again.',
        'error'
      );
    }
  };

  // Column definitions with sortable property
  const columns = [
    { id: "title", label: "Course Name", sortable: true },
    { id: "category_id", label: "Category", sortable: true },
    { id: "duration", label: "Duration", sortable: true },
    { id: "price", label: "Price", sortable: true },
    { id: "level", label: "Level", sortable: true },
    { id: "capacity", label: "Capacity", sortable: true },
    {
      id: "status",
      label: isStudent && activeTab !== "All Courses" ? "Payment" : "Status",
      sortable: true,
    },
  ];

  // Handle sorting when a column header is clicked
  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  // Render sort indicator
  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;

    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp
          className={`${isActive && currentOrderDir === "ASC"
            ? "text-primary"
            : "text-gray-400"
            } -mb-1`}
          size={12}
        />
        <FaSortDown
          className={`${isActive && currentOrderDir === "DESC"
            ? "text-primary"
            : "text-gray-400"
            } -mt-1`}
          size={12}
        />
      </div>
    );
  };

  const getDropdownActions = (id, courseData) => {
    const actions = [];

    if (hasPermission(user, "view_course")) {
      actions.push({
        icon: (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        label: "Preview",
        onClick: () => navigate(`/dashboard/courses/${id}`),
      });
    }

    if (hasPermission(user, "update_course")) {
      actions.push({
        icon: <FiEdit className="w-4 h-4" />,
        label: "Edit",
        onClick: () => navigate(`/dashboard/courses/${id}/edit`),
      });
    }

    if (hasPermission(user, "delete_course")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: () => handleDelete(id),
      });
    }

    return actions;
  };

  const handleRefreshClick = () => {
    if (setReFetchData) {
      setReFetchData(true);
    }
  };

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
              Courses
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Browse, filter, and manage all courses in the platform.
            </p>
          </div>

          {/* Action Buttons: Add Course, Export, and Refresh */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleRefreshClick}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              title="Refresh Data"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            {hasPermission(user, "export_course") && (
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-emerald-600 transition-colors"
                title="Export to Excel"
              >
                <ExcelIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
            {hasPermission(user, "create_course") && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/courses/create')}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-primary-hover transition-colors"
                title="Add New Course"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <svg
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 18 18"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 1v16M1 9h16"
                    />
                  </svg>
                </span>
                <span className="hidden sm:inline">New Course</span>
              </button>
            )}
          </div>
        </div>

        {/* Header: Tabs and Search/Filter */}
        <div className="mt-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-t border-gray-100 pt-4">
          <div className="flex space-x-4 overflow-x-auto">
          {determinedTableTabs.map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                activeTab === tab
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              <span className="font-[400]">{tab}</span>
            </button>
          ))}
        </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            <div className="w-full md:w-[220px]">
              <Dropdown
                items={categories}
                title="All"
                onItemSelect={(selected) => setSelectedItem(selected)}
                id="categories"
                isOpen={activeDropdown === "categories"}
                onToggle={handleDropdownToggle}
              />
            </div>
            <div className="relative w-full sm:w-56">
              <input
                type="text"
                placeholder="Search courses..."
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
              />
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[900px]">
              {loader && (
                <div className="flex justify-center items-center py-6">
                  <Loader />
                </div>
              )}
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
            <tr>
              {columns.map(
                (column, idx) => (
                  <th
                    key={idx}
                    className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide ${
                      currentOrderBy === column.id
                        ? "text-primary"
                        : "text-gray-500"
                    } border-b border-gray-100 ${
                      column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                    }`}
                    onClick={() =>
                      column.sortable && handleSortClick(column.id)
                    }
                    >
                  <div className="flex items-center gap-1">
                      <span>{column.label}</span>
                      {column.sortable && renderSortIndicator(column.id)}
                    </div>
                  </th>
                )
              )}
              {(hasPermission(user, "update_course") ||
                hasPermission(user, "delete_course") ||
                hasPermission(user, "view_course")) && (
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {courses.length === 0 && !loader ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-4 sm:px-6 py-10 text-center text-gray-500"
                >
                  No courses found
                </td>
              </tr>
            ) : (
              courses.map((course, index) => {
                // Allow click only for users with course view permission or students (for their courses)
                const canViewCourse =
                  hasPermission(user, "view_course") ||
                  (isStudent && (activeTab === "My Courses" || activeTab === "All Courses"));
                const hasVerifiedPayment =
                  !isStudent || activeTab !== "My Courses" || course.payment_status === "verified";
                const canOpenCourseContent = canViewCourse && hasVerifiedPayment;

                const handleRowClick = () => {
                  if (!canOpenCourseContent) {
                    if (isStudent && activeTab === "My Courses" && course.payment_status !== "verified") {
                      showToast(
                        "Payment Pending",
                        "Your course payment is still pending verification. You cannot open course lessons yet.",
                        "info"
                      );
                    }
                    return;
                  }
                  // Students browsing "All Courses" should open details/enrollment flow.
                  if (isStudent && activeTab === "All Courses") {
                    navigate(`/dashboard/courses/details/${course.id}`, {
                      state: { course },
                    });
                    return;
                  }

                  // My Courses (or staff views) open lessons/content.
                  navigate(`/dashboard/courses/lesson/${course.id}`, {
                    state: { course },
                  });
                };

                return (
                  <tr
                    key={index}
                    className="group hover:bg-gray-50 transition-colors text-sm cursor-pointer"
                    onClick={handleRowClick}
                  >
                  <td className="px-4 sm:px-6 py-4">
                    <span className="font-medium text-gray-900 group-hover:text-primary transition-colors">
                      {course.title}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    {course.category?.title || course.category_title || '-'}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{course.duration || '-'}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{course.price || '-'}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{course.level || '-'}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">{course.capacity || '-'}</td>
                  <td className="px-4 sm:px-6 py-4">
                    {isStudent && activeTab !== "All Courses" ? (
                      <div className="flex items-center justify-center gap-2">
                        {/* Payment status indicator for student's enrolled courses */}
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          course.payment_status === "verified"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {course.payment_status === "verified" ? (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Verified
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Pending
                            </>
                          )}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <img
                          src={
                            course.status === "active"
                              ? BlueDot
                              : course.status === "disabled"
                              ? GrayDot
                              : OrangeDot
                          }
                          alt={course.status}
                          className="w-3 h-3"
                        />
                        <span className={`text-sm font-medium capitalize ${
                          course.status === "active"
                            ? "text-blue-600"
                            : course.status === "disabled"
                            ? "text-gray-500"
                            : "text-orange-500"
                        }`}>
                          {course.status || '-'}
                        </span>
                      </div>
                    )}
                  </td>
                  {(hasPermission(user, "update_course") ||
                    hasPermission(user, "delete_course") ||
                    hasPermission(user, "view_course")) && (
                    <td className="px-4 sm:px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <ActionsDropdown actions={getDropdownActions(course.id, course)} />
                    </td>
                  )}
                </tr>
              )})
            )}
          </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <p className="hidden sm:block text-xs text-gray-500">
            Showing page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CourseTable;
